/**
 * Safe localStorage wrapper with error handling
 */
export class Storage {
  private static isSupported(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Set item in localStorage
   */
  static setItem(key: string, value: unknown): boolean {
    if (!this.isSupported()) {
      console.warn('localStorage is not supported');
      return false;
    }

    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error('Failed to set localStorage item:', error);
      return false;
    }
  }

  /**
   * Get item from localStorage
   */
  static getItem<T = unknown>(key: string): T | null {
    if (!this.isSupported()) {
      return null;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return null;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error('Failed to get localStorage item:', error);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  static removeItem(key: string): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Failed to remove localStorage item:', error);
      return false;
    }
  }

  /**
   * Clear all localStorage items
   */
  static clear(): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
      return false;
    }
  }

  /**
   * Check if key exists in localStorage
   */
  static hasItem(key: string): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  }
}

/**
 * Safe sessionStorage wrapper with error handling
 */
export class SessionStorage {
  private static isSupported(): boolean {
    try {
      const test = '__session_storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Set item in sessionStorage
   */
  static setItem(key: string, value: unknown): boolean {
    if (!this.isSupported()) {
      console.warn('sessionStorage is not supported');
      return false;
    }

    try {
      const serializedValue = JSON.stringify(value);
      sessionStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error('Failed to set sessionStorage item:', error);
      return false;
    }
  }

  /**
   * Get item from sessionStorage
   */
  static getItem<T = unknown>(key: string): T | null {
    if (!this.isSupported()) {
      return null;
    }

    try {
      const item = sessionStorage.getItem(key);
      if (item === null) {
        return null;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error('Failed to get sessionStorage item:', error);
      return null;
    }
  }

  /**
   * Remove item from sessionStorage
   */
  static removeItem(key: string): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Failed to remove sessionStorage item:', error);
      return false;
    }
  }

  /**
   * Clear all sessionStorage items
   */
  static clear(): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.error('Failed to clear sessionStorage:', error);
      return false;
    }
  }

  /**
   * Check if key exists in sessionStorage
   */
  static hasItem(key: string): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      return sessionStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  }
}
